import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogIn } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1688891648910-1e056d2377c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxub3JkaWMlMjBhYnN0cmFjdCUyMG5hdHVyZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg1MTY1OTg3fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/hjem");
  }, [user, navigate]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!email || !password) {
      setError("Fyll inn e-post og passord.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.user);
      navigate("/hjem");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen bg-background">
      <div className="absolute right-5 top-5 z-20">
        <ThemeToggle />
      </div>

      <div className="flex w-full flex-col justify-center px-6 py-12 sm:px-12 lg:w-[46%] lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mx-auto w-full max-w-sm"
        >
          <Logo subtitle="Styrerom & Aksjeeierbok" />

          <div className="mt-12">
            <h1 className="font-headings text-3xl font-extrabold tracking-tight text-foreground">Logg inn</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Logg inn med e-post og passord. Identiteten din verifiseres med BankID første gang du legger til et selskap.
            </p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-medium">E-post</Label>
              <Input
                id="email" data-testid="login-email-input" type="email" autoComplete="email"
                placeholder="din@epost.no" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-medium">Passord</Label>
              <Input
                id="password" data-testid="login-password-input" type="password" autoComplete="current-password"
                placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
              />
            </div>

            <div className="-mt-1 flex justify-end">
              <Link to="/glemt-passord" data-testid="login-forgot-password-link" className="text-xs font-medium text-muted-foreground transition-colors hover:text-primary hover:underline">
                Glemt passord?
              </Link>
            </div>

            {error ? <p data-testid="login-error" className="text-sm text-destructive">{error}</p> : null}

            <Button type="submit" data-testid="login-submit-button" disabled={loading} className="h-11 w-full gap-2 text-sm font-semibold active:scale-[0.98]">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
              {loading ? "Logger inn …" : "Logg inn"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            Har du ikke konto?{" "}
            <Link to="/register" data-testid="login-register-link" className="font-semibold text-primary hover:underline">Registrer deg</Link>
          </p>
        </motion.div>
      </div>

      <div className="relative hidden lg:block lg:w-[54%]">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/70 via-primary/30 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="max-w-md text-white"
          >
            <h2 className="font-headings text-4xl font-extrabold leading-tight tracking-tight">Gi styret et ordentlig hjem.</h2>
            <p className="mt-4 text-base text-white/85">
              Møter, protokoller, signering og aksjeeierbok — samlet på ett trygt sted. Roller hentes automatisk fra Brønnøysundregisteret og Proff.
            </p>
            <div className="mt-8 flex flex-wrap gap-2 text-xs">
              {["Møter & protokoller", "Digital signering", "Aksjeeierbok", "AI-oppsummering"].map((t) => (
                <span key={t} className="rounded-full border border-white/25 bg-white/10 px-3 py-1 backdrop-blur-sm">{t}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
